<template>
  <div class="opc-foldable" :class="{'is-active': modelValue}">
    <slot name="banner" />
    <zm-accordion ref="collapse" :modelValue="val">
      <zm-accordion-item :name="collapseName" tabindex="-1">
        <template #title>
          <div data-testid="checkout-div-18"  @click.stop="onToggle" @keydown.enter.stop="onToggle" :aria-hidden="titleAriaHidden ? 'true' : 'false'">
            <div v-if="$slots.head" class="opc-foldable__head" @click.stop @keydown.enter.stop @keydown.space.stop>
              <slot name="head" />
            </div>
            <div data-testid="checkout-div-19" v-if="$slots.trigger" class="opc-foldable__trigger" @click.stop="onToggle" @keydown.enter.stop="onToggle">
              <slot name="trigger" />
            </div>
            <span ref="foldableActionRef" class="opc-foldable__action" :class="{'opc-foldable__action--top': actionAlignTop}" :tabindex="arrow?'0':'-1'" role="button" :aria-label="iconAriaLabel" :aria-expanded="String(modelValue)">
              <span v-if="$slots.action" @click.stop>
                <slot name="action" />
              </span>
              <span
                v-else
                data-testid="checkout-span-20"
                tabindex="-1"
                role="button"
                :aria-label="editAriaLabel"
                :aria-expanded="String(modelValue)"
                @click.stop="onToggle"
                v-show="edit"
                class="opc-foldable__action__edit"
              >
                {{ this.editLabel || $t('billing.common.edit') }}
              </span>
              <span data-testid="checkout-span-21" tabindex="-1" v-if="arrow" @click.stop="onToggle" @keydown.enter.stop="onToggle" class="opc-foldable__action__icon"
                role="button"
                :aria-expanded="String(modelValue)"
              >
                <zm-icon><ChevronUpV2 /></zm-icon>
              </span>
            </span>
          </div>
        </template>
        <slot />
      </zm-accordion-item>
    </zm-accordion>
    <div v-if="$slots.collapse && !modelValue">
      <slot name="collapse" />
    </div>
  </div>
</template>

<script>
/*
 * $slots.action: triggers the collapse status
 * $slots.head: the fixed view part beyond collapse
 * $slots.action: actions that will override the default edit button. normally we won't use it
 */
import { ChevronUpV2 } from '@zoom/icons-vue'
export default {
  components: { ChevronUpV2 },
  props: {
    arrow: {
      // show top-right drop down arrow
      type: Boolean,
      default: true,
    },
    edit: {
      // show default edit button, will be overwriten by $slots.action
      type: Boolean,
      default: true,
    },
    editLabel: {
      // default edit button label
      type: String,
    },
    modelValue: {
      // used in v-model
      type: Boolean,
      default: false,
    },
    editAriaLabel: {
      type: String,
      default: 'Edit',
    },
    title: {
      type: String,
      default: '',
    },
    isRemoveWrapAriaLabelledby: {
      type: Boolean,
      default: false,
    },
    titleAriaHidden: {
      type: Boolean,
      default: false,
    },
    actionAlignTop: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      collapseName: 'x',
    };
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    val() {
      return [this.modelValue ? this.collapseName : ''];
    },
    iconAriaLabel() {
      return this.title ? `${(this.modelValue ? '' : 'Edit ')}${this.title} plan` : this.editAriaLabel;
    },
  },
  methods: {
    onToggle() {
      this.$emit('update:modelValue', !this.modelValue);
      this.$emit('change', !this.modelValue);
      if (this.$context?.is.zs2ze) {
        this.modelValue === false && this.title && this.$tracking.buyInteraction('product details accordion', `zoom events - edit`);
      } else {
        this.modelValue === false && this.title && this.$tracking.buyInteraction(this.title, `${this.title} -edit`);
      }
    },
    reduceCollapseAx(){
      const headerDom = this.$el.querySelector('.zbo-accordion-item__trigger');
      headerDom?.removeAttribute('tabindex');
      headerDom?.removeAttribute('role');
      this.$refs.collapse.$el.removeAttribute('role');
    },
    // In windows, it would cause NVDA read redundant info "[plan names].property page" [ZOOM-553124]
    removeRedundantAxMessage(){
      const headerDom = this.$el.querySelector('.zbo-collapse-item__wrap');
      headerDom?.removeAttribute('aria-labelledby');
    },
  },
  mounted() {
    this.reduceCollapseAx();
    if(this.isRemoveWrapAriaLabelledby) {
      this.removeRedundantAxMessage();
    }
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-foldable {
  $theme-color: $theme-color;
  $gap: 9px;
  $head-height: 18px;
  &__trigger {
  }
  &__action {
    &--top {
      top: 0;
      transform: translateY(0);
    }
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    z-index: 1;
    // padding-right: $gap;
    font-weight: 400;
    font-size: 14px;
    line-height: 1;
    color: var(--theme-color, var(--zoom-color-text-primary, #0D6BDE));
    user-select: none;
    padding-left: 4px;
    background: var(--zoom-color-bg-default, #FFFFFF);;
    border-radius: 6px;
    height: fit-content;

    @include media(mobile) {
      top: 0;
      transform: translateY(0);
    }

    span {
      margin-left: 16px;
    }
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px #fff, 0 0 0 4px $focus-color;
      border-radius: 1px;
    }
    &__edit {
      transition: all 0.3s ease;
      color: $theme-color;
      &:hover {
        color: var(--theme-color, var(--zoom-color-text-primary, #0D6BDE));
      }
    }
    &__icon {
      display: inline-block;
      margin-left: $gap;
      transition: all 0.3s ease;
      position: relative;
      transform: rotate(180deg);
    }

    @include media(mobile) {
      font-size: 14px;
      // padding-right: $gap;
      background: transparent;
      span {
        margin-left: 16px;
      }
    }
  }
  &__head {
    position: relative;
    cursor: default;
    min-height: $head-height;
  }
  .zbo-accordion {
    border-top: none;
    border-bottom: none;
    &-item {
      position: relative;
      border-bottom: none !important;
      &__trigger {
        display: block;
        width: 100%;
        padding: 0;
        min-height: $head-height;
        &:hover:not(.is-disabled) {
          background-color: unset !important;
        }
      }
      &__title {
        display: block;
        position: relative;
      }
      &__region {
        margin: 0;
        overflow: visible;
      }
      &__content {
        padding-bottom: 0;
      }
      &__arrow {
        display: none;
      }

      .opc-foldable__action__icon {
        padding: 0px;
        margin-left: 4px;
      }

      &.is-active {
        .opc-foldable__action__edit {
          display: none;
        }
        .opc-foldable__action__icon {
          transform: rotate(0deg);
        }
      }
    }
  }
  &__heading{
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: 35px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
  }
  &__text{
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    color: var(--zoom-color-text-strong-neutral, #555B62);
  }
  &__space{
    margin-bottom: 12px;
  }
  &__space2x{
    margin-bottom: 24px;
  }
}
</style>
